/**
 * CategoryPicker — horizontal chip row for selecting a dispute category.
 *
 * Mirrors the 8-category enum in rental_disputes.category. Icons come
 * from lucide-react-native; the picker is scrollable for small screens.
 */
import { ScrollView, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import {
  Car,
  Gauge,
  Fuel,
  Droplets,
  Clock,
  PackageX,
  Receipt,
  CircleAlert,
  type LucideIcon,
} from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import type { DisputeCategory } from '@/lib/types/dispute.types'

const CATEGORIES: { value: DisputeCategory; icon: LucideIcon }[] = [
  { value: 'vehicle_damage', icon: Car },
  { value: 'excessive_mileage', icon: Gauge },
  { value: 'fuel_level', icon: Fuel },
  { value: 'cleanliness', icon: Droplets },
  { value: 'late_return', icon: Clock },
  { value: 'missing_items', icon: PackageX },
  { value: 'billing', icon: Receipt },
  { value: 'other', icon: CircleAlert },
]

interface CategoryPickerProps {
  value: DisputeCategory | null
  onChange: (value: DisputeCategory) => void
}

export function CategoryPicker({ value, onChange }: CategoryPickerProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: SPACING.sm, paddingVertical: SPACING.xs }}
    >
      {CATEGORIES.map(({ value: cat, icon: Icon }) => {
        const selected = value === cat
        return (
          <Pressable
            key={cat}
            onPress={() => onChange(cat)}
            accessibilityRole="button"
            accessibilityLabel={t(`disputes.category_${cat}`)}
            accessibilityState={{ selected }}
            testID={`disputes.category-${cat}`}
            style={{
              minWidth: 96,
              paddingVertical: SPACING.md,
              paddingHorizontal: SPACING.md,
              borderRadius: RADIUS.lg,
              backgroundColor: selected ? `${COLORS.purple.DEFAULT}14` : colors.surface,
              borderWidth: 1.5,
              borderColor: selected ? COLORS.purple.DEFAULT : colors.border,
              alignItems: 'center',
              gap: SPACING.xs,
            }}
          >
            <Icon
              size={22}
              color={selected ? COLORS.purple.DEFAULT : colors.textSecondary}
              strokeWidth={1.8}
            />
            <Text
              style={{
                fontSize: 12,
                fontFamily: selected ? FONTS.bodyBold : FONTS.body,
                color: selected ? colors.textBrand : colors.textSecondary,
                textAlign: 'center',
              }}
              numberOfLines={2}
            >
              {t(`disputes.category_${cat}`)}
            </Text>
          </Pressable>
        )
      })}
    </ScrollView>
  )
}
